import { useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { useIntl } from "react-intl";
import {
  INSTAGRAM_LOGIN_GET_LOADING,
  INSTAGRAM_LOGIN_SYNC_LOADING,
  INSTAGRAM_POST_LOADING,
} from "../../constants/loading.constant";
import { useGlobalCore } from "../globals";
import { instagramLoginApiService } from "./services/api";
import {
  getInstagramLoginFailureAction,
  getInstagramLoginInitAction,
  getInstagramLoginSuccessAction,
  getInstagramLoginUrlFailureAction,
  getInstagramLoginUrlInitAction,
  getInstagramLoginUrlSuccessAction,
  postBindInstagramLoginFailureAction,
  postBindInstagramLoginInitAction,
  postBindInstagramLoginSuccessAction,
  postUnbindInstagramLoginFailureAction,
  postUnbindInstagramLoginInitAction,
  postUnbindInstagramLoginSuccessAction,
} from "./store/api-actions";
import { loginUrlSelector, settingSelector } from "./store/selector";
import { Mentions } from "../socialPost/interfaces/Mention";
import { PostImage } from "../socialPost/interfaces/postImage";
import { PostVideo } from "../socialPost/interfaces/postVideo";
import { Media } from "../socialPost/interfaces/api/get-social-posts";
import { dispatchOptimisticScheduledPostIfNeeded } from "../socialPost/helpers/scheduleResponseToSocialPost";

export function useInstagramLoginCore() {
  const intl = useIntl();
  const dispatch = useDispatch();
  const settings = useSelector(settingSelector);
  const loginUrl = useSelector(loginUrlSelector);
  const { turnOnLoading, turnOffLoading } = useGlobalCore();

  const unbind = useCallback(
    async (companyId: string, onSuccess?: () => void) => {
      try {
        dispatch(postUnbindInstagramLoginInitAction());
        turnOnLoading(INSTAGRAM_LOGIN_SYNC_LOADING);
        await instagramLoginApiService.unbind(companyId);
        dispatch(postUnbindInstagramLoginSuccessAction());
        turnOffLoading(INSTAGRAM_LOGIN_SYNC_LOADING, 500);
        toast.success(intl.formatMessage({ id: "app.toast.sucess12" }));
        onSuccess?.();
      } catch (error: any) {
        turnOffLoading(INSTAGRAM_LOGIN_SYNC_LOADING);
        toast.error(intl.formatMessage({ id: "app.toast.error26" }));
        console.error(error);
        dispatch(
          postUnbindInstagramLoginFailureAction({ error: String(error) })
        );
      }
    },
    [dispatch, turnOffLoading, turnOnLoading, intl]
  );

  const bind = useCallback(
    async (companyId: string, code: string, onSuccess?: () => void, state?: string) => {
      try {
        dispatch(postBindInstagramLoginInitAction());
        turnOnLoading(INSTAGRAM_LOGIN_SYNC_LOADING);
        const response = await instagramLoginApiService.bind(companyId, {
          code,
          state,
        });
        dispatch(postBindInstagramLoginSuccessAction({ settings: response }));
        turnOffLoading(INSTAGRAM_LOGIN_SYNC_LOADING, 500);
        toast.success(intl.formatMessage({ id: "app.instagramLogin.toast.success" }));
        onSuccess?.();
      } catch (error: any) {
        turnOffLoading(INSTAGRAM_LOGIN_SYNC_LOADING);
        toast.error(intl.formatMessage({ id: "app.instagramLogin.toast.error" }));
        console.error(error);
        dispatch(
          postBindInstagramLoginFailureAction({ error: String(error) })
        );
      }
    },
    [dispatch, turnOffLoading, turnOnLoading, intl]
  );

  const get = useCallback(
    async (companyId: string) => {
      try {
        dispatch(getInstagramLoginInitAction());
        turnOnLoading(INSTAGRAM_LOGIN_GET_LOADING);
        const response = await instagramLoginApiService.get(companyId);
        dispatch(getInstagramLoginSuccessAction({ settings: response }));
        turnOffLoading(INSTAGRAM_LOGIN_GET_LOADING, 500);
      } catch (error: any) {
        turnOffLoading(INSTAGRAM_LOGIN_GET_LOADING);
        console.error(error);
        dispatch(getInstagramLoginFailureAction({ error: String(error) }));
      }
    },
    [dispatch, turnOffLoading, turnOnLoading]
  );

  const login = useCallback(
    async (companyId: string) => {
      try {
        dispatch(getInstagramLoginUrlInitAction());
        turnOnLoading(INSTAGRAM_LOGIN_SYNC_LOADING);
        const response = await instagramLoginApiService.login(companyId);
        dispatch(getInstagramLoginUrlSuccessAction({ url: response.url }));
        turnOffLoading(INSTAGRAM_LOGIN_SYNC_LOADING);
        return response.url;
      } catch (error: any) {
        turnOffLoading(INSTAGRAM_LOGIN_SYNC_LOADING);
        toast.error(intl.formatMessage({ id: "app.instagramLogin.toast.error" }));
        console.error(error);
        dispatch(getInstagramLoginUrlFailureAction({ error: String(error) }));
      }
    },
    [dispatch, turnOffLoading, turnOnLoading, intl]
  );

  const notifyQueued = useCallback((_scheduleDate: string) => {
    // ModernPostButtons owns the user-facing success toast (one message per action).
  }, []);

  const socialPost = useCallback(
    async (
      companyId: string,
      text: string,
      mentions: Mentions[],
      images: PostImage[],
      scheduleDate: string,
      repostId: string,
      onSuccess?: () => void
    ) => {
      try {
        turnOnLoading(INSTAGRAM_POST_LOADING);
        const data = await instagramLoginApiService.socialPost(
          text,
          mentions,
          images,
          scheduleDate,
          repostId,
          companyId
        );
        turnOffLoading(INSTAGRAM_POST_LOADING);
        notifyQueued(scheduleDate);
        dispatchOptimisticScheduledPostIfNeeded(dispatch, data, {
          scheduleDate,
          companyId,
          defaultProvider: "instagramLogin",
          postType: images.length > 0 ? "image" : "text",
          text,
        });
        onSuccess?.();
      } catch (error: any) {
        turnOffLoading(INSTAGRAM_POST_LOADING);
        toast.error(
          scheduleDate
            ? intl.formatMessage({ id: "app.toast.error29" })
            : intl.formatMessage({ id: "app.toast.error30" })
        );
        console.error(error);
      }
    },
    [dispatch, turnOffLoading, turnOnLoading, intl, notifyQueued]
  );

  const socialPostVideo = useCallback(
    async (
      companyId: string,
      text: string,
      mentions: Mentions[],
      video: PostVideo,
      scheduleDate: string,
      repostId: string,
      onSuccess?: () => void
    ) => {
      try {
        turnOnLoading(INSTAGRAM_POST_LOADING);
        const data = await instagramLoginApiService.socialPostVideo(
          text,
          mentions,
          video,
          scheduleDate,
          repostId,
          companyId
        );
        turnOffLoading(INSTAGRAM_POST_LOADING);
        notifyQueued(scheduleDate);
        dispatchOptimisticScheduledPostIfNeeded(dispatch, data, {
          scheduleDate,
          companyId,
          defaultProvider: "instagramLogin",
          postType: "video",
          text,
        });
        onSuccess?.();
      } catch (error: any) {
        turnOffLoading(INSTAGRAM_POST_LOADING);
        toast.error(
          scheduleDate
            ? intl.formatMessage({ id: "app.toast.error29" })
            : intl.formatMessage({ id: "app.toast.error30" })
        );
        console.error(error);
      }
    },
    [dispatch, turnOffLoading, turnOnLoading, intl, notifyQueued]
  );

  const modifySocialPost = useCallback(
    async (
      companyId: string,
      postId: string,
      text: string,
      mentions: Mentions[],
      images: PostImage[],
      scheduleDate: string,
      media: Media[],
      onSuccess?: () => void
    ) => {
      try {
        turnOnLoading(INSTAGRAM_POST_LOADING);
        await instagramLoginApiService.putSocialPost(
          postId,
          text,
          mentions,
          images,
          scheduleDate,
          companyId,
          media
        );
        turnOffLoading(INSTAGRAM_POST_LOADING);
        notifyQueued(scheduleDate);
        onSuccess?.();
      } catch (error: any) {
        turnOffLoading(INSTAGRAM_POST_LOADING);
        toast.error(intl.formatMessage({ id: "app.toast.error29" }));
        console.error(error);
      }
    },
    [turnOffLoading, turnOnLoading, intl, notifyQueued]
  );

  const modifySocialPostVideo = useCallback(
    async (
      companyId: string,
      postId: string,
      text: string,
      mentions: Mentions[],
      video: PostVideo | null,
      scheduleDate: string,
      media: Media[],
      onSuccess?: () => void
    ) => {
      try {
        turnOnLoading(INSTAGRAM_POST_LOADING);
        await instagramLoginApiService.putSocialPostVideo(
          postId,
          text,
          mentions,
          video,
          scheduleDate,
          companyId,
          media
        );
        turnOffLoading(INSTAGRAM_POST_LOADING);
        notifyQueued(scheduleDate);
        onSuccess?.();
      } catch (error: any) {
        turnOffLoading(INSTAGRAM_POST_LOADING);
        toast.error(intl.formatMessage({ id: "app.toast.error29" }));
        console.error(error);
      }
    },
    [turnOffLoading, turnOnLoading, intl, notifyQueued]
  );

  return {
    settings,
    loginUrl,
    unbind,
    bind,
    get,
    login,
    socialPost,
    socialPostVideo,
    modifySocialPost,
    modifySocialPostVideo,
  };
}
